// HR Portal mock data
import { mockUsers } from '../../mockData';

export interface Department {
  id: string;
  name: string;
  headId: string;
  headName: string;
  employeeCount: number;
  budget: number;
  color: string;
}

export interface LeaveRequest {
  id: string;
  employeeId: string;
  employeeName: string;
  type: 'vacation' | 'sick' | 'personal' | 'parental' | 'bereavement';
  startDate: Date;
  endDate: Date;
  days: number;
  status: 'pending' | 'approved' | 'rejected';
  reason: string;
  approverIds: string[];
  approverNames: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high';
  authorId: string;
  authorName: string;
  createdAt: Date;
  expiresAt?: Date;
}

export interface OrgNode {
  id: string;
  name: string;
  title: string;
  department: string;
  avatar?: string;
  email: string;
  children?: OrgNode[];
}

export const mockDepartments: Department[] = [
  {
    id: 'dept-1',
    name: 'Engineering',
    headId: 'u2',
    headName: 'Jane Smith',
    employeeCount: 15,
    budget: 2500000,
    color: 'blue',
  },
  {
    id: 'dept-2',
    name: 'Design',
    headId: 'u10',
    headName: 'Hannah Davis',
    employeeCount: 5,
    budget: 800000,
    color: 'pink',
  },
  {
    id: 'dept-3',
    name: 'Product',
    headId: 'u6',
    headName: 'Diana Prince',
    employeeCount: 4,
    budget: 600000,
    color: 'violet',
  },
  {
    id: 'dept-4',
    name: 'Sales',
    headId: 'u15',
    headName: 'Michael Scott',
    employeeCount: 4,
    budget: 1200000,
    color: 'green',
  },
  {
    id: 'dept-5',
    name: 'Marketing',
    headId: 'u12',
    headName: 'Julia Roberts',
    employeeCount: 2,
    budget: 500000,
    color: 'orange',
  },
  {
    id: 'dept-6',
    name: 'Human Resources',
    headId: 'u14',
    headName: 'Laura Palmer',
    employeeCount: 2,
    budget: 300000,
    color: 'teal',
  },
  {
    id: 'dept-7',
    name: 'Finance',
    headId: 'u17',
    headName: 'Oscar Martinez',
    employeeCount: 2,
    budget: 400000,
    color: 'yellow',
  },
  {
    id: 'dept-8',
    name: 'Customer Success',
    headId: 'u22',
    headName: 'Tina Turner',
    employeeCount: 1,
    budget: 200000,
    color: 'cyan',
  },
];

export const mockLeaveRequests: LeaveRequest[] = [
  {
    id: 'leave-1',
    employeeId: 'u1',
    employeeName: 'John Doe',
    type: 'vacation',
    startDate: new Date('2024-02-15'),
    endDate: new Date('2024-02-22'),
    days: 6,
    status: 'pending',
    reason: 'Family vacation to Hawaii',
    approverIds: ['u2'],
    approverNames: ['Jane Smith'],
    createdAt: new Date('2024-01-18'),
    updatedAt: new Date('2024-01-18'),
  },
  {
    id: 'leave-2',
    employeeId: 'u5',
    employeeName: 'Charlie Brown',
    type: 'sick',
    startDate: new Date('2024-01-22'),
    endDate: new Date('2024-01-23'),
    days: 2,
    status: 'approved',
    reason: 'Not feeling well, doctor appointment',
    approverIds: ['u2'],
    approverNames: ['Jane Smith'],
    createdAt: new Date('2024-01-21'),
    updatedAt: new Date('2024-01-21'),
  },
  {
    id: 'leave-3',
    employeeId: 'u8',
    employeeName: 'Fiona Green',
    type: 'personal',
    startDate: new Date('2024-01-25'),
    endDate: new Date('2024-01-25'),
    days: 1,
    status: 'approved',
    reason: 'Personal errands',
    approverIds: ['u10'],
    approverNames: ['Hannah Davis'],
    createdAt: new Date('2024-01-19'),
    updatedAt: new Date('2024-01-20'),
  },
  {
    id: 'leave-4',
    employeeId: 'u16',
    employeeName: 'Nina Anderson',
    type: 'vacation',
    startDate: new Date('2024-03-01'),
    endDate: new Date('2024-03-08'),
    days: 6,
    status: 'pending',
    reason: 'Spring break trip',
    approverIds: ['u2'],
    approverNames: ['Jane Smith'],
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-01-20'),
  },
  {
    id: 'leave-5',
    employeeId: 'u13',
    employeeName: 'Kevin Hart',
    type: 'parental',
    startDate: new Date('2024-02-01'),
    endDate: new Date('2024-02-14'),
    days: 10,
    status: 'approved',
    reason: 'Paternity leave - new baby',
    approverIds: ['u15'],
    approverNames: ['Michael Scott'],
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-16'),
  },
  {
    id: 'leave-6',
    employeeId: 'u9',
    employeeName: 'George Miller',
    type: 'sick',
    startDate: new Date('2024-01-19'),
    endDate: new Date('2024-01-19'),
    days: 1,
    status: 'approved',
    reason: 'Flu symptoms',
    approverIds: ['u2'],
    approverNames: ['Jane Smith'],
    createdAt: new Date('2024-01-19'),
    updatedAt: new Date('2024-01-19'),
  },
  {
    id: 'leave-7',
    employeeId: 'u30',
    employeeName: 'Dwight Schrute',
    type: 'vacation',
    startDate: new Date('2024-01-29'),
    endDate: new Date('2024-01-31'),
    days: 3,
    status: 'rejected',
    reason: 'Beet farming conference',
    approverIds: ['u15'],
    approverNames: ['Michael Scott'],
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-12'),
  },
  {
    id: 'leave-8',
    employeeId: 'u3',
    employeeName: 'Alice Johnson',
    type: 'bereavement',
    startDate: new Date('2024-01-24'),
    endDate: new Date('2024-01-26'),
    days: 3,
    status: 'approved',
    reason: 'Family bereavement',
    approverIds: ['u10'],
    approverNames: ['Hannah Davis'],
    createdAt: new Date('2024-01-23'),
    updatedAt: new Date('2024-01-23'),
  },
];

export const mockAnnouncements: Announcement[] = [
  {
    id: 'ann-1',
    title: 'Q1 2024 All-Hands Meeting',
    content: 'Please join us for our quarterly all-hands meeting on February 5th at 2:00 PM. We will review Q4 results and discuss our Q1 goals.',
    priority: 'high',
    authorId: 'u14',
    authorName: 'Laura Palmer',
    createdAt: new Date('2024-01-20'),
    expiresAt: new Date('2024-02-05'),
  },
  {
    id: 'ann-2',
    title: 'New Health Benefits Available',
    content: 'We are excited to announce enhanced health benefits starting February 1st. Check your email for details on the new dental and vision plans.',
    priority: 'medium',
    authorId: 'u14',
    authorName: 'Laura Palmer',
    createdAt: new Date('2024-01-18'),
  },
  {
    id: 'ann-3',
    title: 'Office Closed - Presidents Day',
    content: 'A reminder that our office will be closed on Monday, February 19th for Presidents Day. Enjoy the long weekend!',
    priority: 'low',
    authorId: 'u14',
    authorName: 'Laura Palmer',
    createdAt: new Date('2024-01-15'),
    expiresAt: new Date('2024-02-20'),
  },
  {
    id: 'ann-4',
    title: 'Welcome New Team Members',
    content: 'Please join us in welcoming our new team members: Zack Morris (Engineering Intern) and Victor Stone (AI/ML Engineer). Say hello when you see them!',
    priority: 'medium',
    authorId: 'u14',
    authorName: 'Laura Palmer',
    createdAt: new Date('2024-01-10'),
  },
];

// Org chart data structure
export const mockOrgChart: OrgNode = {
  id: 'ceo',
  name: 'CEO',
  title: 'Chief Executive Officer',
  department: 'Executive',
  email: 'ceo@company.com',
  children: [
    {
      id: 'u2',
      name: 'Jane Smith',
      title: 'Engineering Manager',
      department: 'Engineering',
      avatar: '/avatars/2.jpg',
      email: 'jane.smith@company.com',
      children: [
        { id: 'u1', name: 'John Doe', title: 'Senior Frontend Developer', department: 'Engineering', avatar: '/avatars/1.jpg', email: 'john.doe@company.com' },
        { id: 'u4', name: 'Bob Williams', title: 'Backend Developer', department: 'Engineering', avatar: '/avatars/4.jpg', email: 'bob.williams@company.com' },
        { id: 'u5', name: 'Charlie Brown', title: 'Full Stack Developer', department: 'Engineering', avatar: '/avatars/5.jpg', email: 'charlie.brown@company.com' },
        { id: 'u7', name: 'Ethan Hunt', title: 'QA Lead', department: 'Engineering', avatar: '/avatars/7.jpg', email: 'ethan.hunt@company.com' },
        { id: 'u11', name: 'Ian Foster', title: 'DevOps Engineer', department: 'Engineering', avatar: '/avatars/11.jpg', email: 'ian.foster@company.com' },
      ],
    },
    {
      id: 'u10',
      name: 'Hannah Davis',
      title: 'Design Manager',
      department: 'Design',
      avatar: '/avatars/10.jpg',
      email: 'hannah.davis@company.com',
      children: [
        { id: 'u3', name: 'Alice Johnson', title: 'Senior UI/UX Designer', department: 'Design', avatar: '/avatars/3.jpg', email: 'alice.johnson@company.com' },
        { id: 'u8', name: 'Fiona Green', title: 'Product Designer', department: 'Design', avatar: '/avatars/8.jpg', email: 'fiona.green@company.com' },
        { id: 'u18', name: 'Pam Beesly', title: 'Graphic Designer', department: 'Design', avatar: '/avatars/18.jpg', email: 'pam.beesly@company.com' },
      ],
    },
    {
      id: 'u15',
      name: 'Michael Scott',
      title: 'Sales Manager',
      department: 'Sales',
      avatar: '/avatars/15.jpg',
      email: 'michael.scott@company.com',
      children: [
        { id: 'u13', name: 'Kevin Hart', title: 'Account Executive', department: 'Sales', avatar: '/avatars/13.jpg', email: 'kevin.hart@company.com' },
        { id: 'u30', name: 'Dwight Schrute', title: 'Senior Account Executive', department: 'Sales', avatar: '/avatars/30.jpg', email: 'dwight.schrute@company.com' },
      ],
    },
    {
      id: 'u14',
      name: 'Laura Palmer',
      title: 'HR Manager',
      department: 'Human Resources',
      avatar: '/avatars/14.jpg',
      email: 'laura.palmer@company.com',
    },
  ],
};

// HR stats
export const hrStats = {
  totalEmployees: 30,
  employeesChange: 3,
  openPositions: 5,
  pendingLeaves: 2,
  avgTenure: 2.5,
  turnoverRate: 8.5,
  departmentCount: 8,
  upcomingBirthdays: 4,
};

// Employees by department
export const employeesByDepartment = [
  { department: 'Engineering', count: 15, color: 'blue' },
  { department: 'Design', count: 5, color: 'pink' },
  { department: 'Product', count: 4, color: 'violet' },
  { department: 'Sales', count: 4, color: 'green' },
  { department: 'Marketing', count: 2, color: 'orange' },
];

// Get employees from existing mock users
export const getEmployees = () => mockUsers;
